import type { CalendarUpsertSchema } from '@repo/schemas/meeting/calendarUpsert';

import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  set,
  startOfDay,
  getYear,
  getMonth,
  getDate,
  differenceInMinutes,
  addMinutes,
  isBefore,
  isAfter,
  subMinutes,
  differenceInDays,
  isValid,
  isSameDay,
} from 'date-fns';
import { CalendarDaysIcon, MinusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';

import MeetingFormContentLabelIcon from '@/components/features/calendar/form/meeting-form-content/MeetingFormContentLabelIcon';
import TimeSelect from '@/components/features/time-range-select/TimeSelect';
import { DatePicker } from '@/components/ui/date-picker';
import { FormControl, FormField, FormItem, ErrorMessage } from '@/components/ui/form';
import { useMediaQuery } from '@/hooks/media-query';
import { DEFAULT_TIME_STEP } from '@/utils/calendar';

const MAX_RANGE_DAYS = 14;

interface MeetingDateAndTimeFieldsProps {
  form: UseFormReturn<CalendarUpsertSchema>;
}

export default function MeetingDateAndTimeFields(meetingDateAndTimeFieldsProps: MeetingDateAndTimeFieldsProps) {
  const { form } = meetingDateAndTimeFieldsProps;
  const breakpoint = useMediaQuery('sm');

  const startDateValue = useWatch({ control: form.control, name: 'startDate' });
  const endDateValue = useWatch({ control: form.control, name: 'endDate' });

  const startDateParsed = useMemo<Date | null>(() => {
    const d = new Date(startDateValue);
    return isValid(d) ? d : null;
  }, [startDateValue]);

  const endDateParsed = useMemo<Date | null>(() => {
    const d = new Date(endDateValue);
    return isValid(d) ? d : null;
  }, [endDateValue]);

  const showEndDate = useMemo<boolean>(() => {
    if (startDateParsed == null || endDateParsed == null) return false;
    return differenceInCalendarDays(endDateParsed, startDateParsed) > 0 || !breakpoint;
  }, [endDateParsed, startDateParsed, breakpoint]);

  const handleStartDatePick = (newDateTime: Date): void => {
    const prevStartDateTime = new Date(form.getValues('startDate'));
    const prevEndDateTime = new Date(form.getValues('endDate'));
    const newStartDateTime = set(prevStartDateTime, { year: getYear(newDateTime), month: getMonth(newDateTime), date: getDate(newDateTime) });
    if (isSameDay(prevStartDateTime, prevEndDateTime)) {
      const newEndDateTime = set(prevEndDateTime, { year: getYear(newStartDateTime), month: getMonth(newStartDateTime), date: getDate(newStartDateTime) });
      form.setValue('endDate', newEndDateTime.toISOString());
    } else if (isBefore(prevEndDateTime, newStartDateTime)) {
      const difference = differenceInDays(prevEndDateTime, prevStartDateTime);
      const newEndDateTime = addDays(newStartDateTime, difference);
      form.setValue('endDate', newEndDateTime.toISOString());
    }
    form.setValue('startDate', newStartDateTime.toISOString());
  };

  const handleEndDatePick = (newDateTime: Date): void => {
    const prevEndDateTime = new Date(form.getValues('endDate'));
    const newEndDateTime = set(prevEndDateTime, { year: getYear(newDateTime), month: getMonth(newDateTime), date: getDate(newDateTime) });
    form.setValue('endDate', newEndDateTime.toISOString());
  };

  const handleStartTimeChange = (newDateTime: Date): void => {
    if (!isValid(newDateTime)) return;
    const prevStartDateTime = new Date(form.getValues('startDate'));
    const prevEndDateTime = new Date(form.getValues('endDate'));

    if (isBefore(prevEndDateTime, newDateTime)) {
      const difference = differenceInMinutes(prevEndDateTime, prevStartDateTime);
      const newEndDateTime = addMinutes(newDateTime, difference);
      form.setValue('endDate', newEndDateTime.toISOString());
    }
    form.setValue('startDate', newDateTime.toISOString());
  };

  const handleEndTimeChange = (newDateTime: Date): void => {
    if (!isValid(newDateTime)) return;
    const prevStartDateTime = new Date(form.getValues('startDate'));
    const prevEndDateTime = new Date(form.getValues('endDate'));

    if (isAfter(prevStartDateTime, newDateTime)) {
      const difference = differenceInMinutes(prevEndDateTime, prevStartDateTime);
      const newStartDateTime = subMinutes(newDateTime, difference);
      form.setValue('startDate', newStartDateTime.toISOString());
    }
    form.setValue('endDate', newDateTime.toISOString());
  };

  return (
    <div className={'flex gap-3 px-4'}>
      <MeetingFormContentLabelIcon>
        <CalendarDaysIcon />
      </MeetingFormContentLabelIcon>
      <fieldset className={'w-full'}>
        <legend className={'mb-1.5 text-base font-normal'}>Data i czas</legend>
        <div className={'flex w-full flex-col gap-4 sm:flex-row sm:gap-1'}>
          <div className={'flex w-full gap-1'}>
            <span className={'text-muted-foreground me-2 flex h-10 items-center text-xs sm:hidden'}>Od</span>
            <FormField
              name={'startDate'}
              control={form.control}
              rules={{ deps: ['endDate'] }}
              render={({ field }) => (
                <FormItem className={'w-full max-w-28 min-w-24'}>
                  <FormControl>
                    <DatePicker
                      {...field}
                      selected={startDateParsed}
                      dateFormat={'PP'}
                      className={'justify-center px-1 text-center text-sm'}
                      showIcon={false}
                      onSelect={(date) => {
                        if (!date) return;
                        handleStartDatePick(date);
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name={'startDate'}
              control={form.control}
              rules={{ deps: ['endDate'] }}
              render={({ field }) => (
                <FormItem className={'w-fit'}>
                  <FormControl>
                    <TimeSelect
                      {...field}
                      value={startDateParsed}
                      onValueChange={(dateTime) => {
                        handleStartTimeChange(dateTime);
                        field.onBlur();
                      }}
                      startDate={startDateParsed ? startOfDay(startDateParsed) : undefined}
                      endDate={startDateParsed ? endOfDay(startDateParsed) : undefined}
                      minutesStep={DEFAULT_TIME_STEP}
                      className={'max-w-17'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <span className={'text-muted-foreground hidden h-10 shrink-0 items-center justify-center sm:flex'}>
            <MinusIcon className={'size-3.5'} aria-hidden="true" />
          </span>
          <div className={'flex w-full flex-row-reverse justify-end gap-1 sm:flex-row sm:justify-start'}>
            <FormField
              name={'endDate'}
              control={form.control}
              rules={{ deps: ['startDate'] }}
              render={({ field }) => (
                <FormItem className={'w-fit'}>
                  <FormControl>
                    <TimeSelect
                      {...field}
                      value={endDateParsed}
                      onValueChange={(dateTime) => {
                        handleEndTimeChange(dateTime);
                        field.onBlur();
                      }}
                      minutesStep={DEFAULT_TIME_STEP}
                      startDate={endDateParsed ? startOfDay(endDateParsed) : undefined}
                      endDate={endDateParsed ? (showEndDate ? endOfDay(endDateParsed) : addDays(endOfDay(endDateParsed), 1)) : undefined}
                      className={'max-w-17'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {showEndDate ? (
              <FormField
                name={'endDate'}
                rules={{ deps: ['startDate'] }}
                control={form.control}
                render={({ field }) => (
                  <FormItem className={'w-full max-w-28 min-w-24'}>
                    <FormControl>
                      <DatePicker
                        {...field}
                        selected={endDateParsed}
                        dateFormat={'PP'}
                        className={'justify-center px-1 text-center text-sm'}
                        showIcon={false}
                        onSelect={(date) => {
                          if (!date) return;
                          handleEndDatePick(date);
                          field.onBlur();
                        }}
                        matcher={
                          startDateParsed
                            ? {
                                before: startOfDay(startDateParsed),
                                after: endOfDay(addDays(startDateParsed, MAX_RANGE_DAYS)),
                              }
                            : undefined
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            ) : null}

            <span className={'text-muted-foreground me-2 flex h-10 items-center text-xs sm:hidden'}>Do</span>
          </div>
        </div>
        <div className="mt-2 flex gap-1">
          <ErrorMessage name="startDate" />
          <ErrorMessage name="endDate" />
        </div>
      </fieldset>
    </div>
  );
}
